// src/contexts/CallContext.js

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebaseConfig';
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    onSnapshot,
    serverTimestamp,
    setDoc,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import useAuth from '../hooks/useAuth';
import webrtcService from '../services/webrtcService';

const CallContext = createContext();

export const useCall = () => {
    const context = useContext(CallContext);
    if (!context) {
        throw new Error('useCall must be used within a CallProvider');
    }
    return context;
};

export const CallProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [callState, setCallState] = useState('idle'); // 'idle' | 'calling' | 'ringing' | 'connected'
    const [activeCall, setActiveCall] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [remoteStream, setRemoteStream] = useState(null);
    const [isSpeakerOn, setIsSpeakerOn] = useState(false);

    const callDocUnsubRef = useRef(null);
    const callerCandidatesUnsubRef = useRef(null);
    const receiverCandidatesUnsubRef = useRef(null);
    const callListenerUnsubRef = useRef(null);
    const remoteAudioRef = useRef(null);

    // Clean up listeners and WebRTC
    const endCallSession = useCallback(async () => {
        if (callDocUnsubRef.current) {
            callDocUnsubRef.current();
            callDocUnsubRef.current = null;
        }
        if (callerCandidatesUnsubRef.current) {
            callerCandidatesUnsubRef.current();
            callerCandidatesUnsubRef.current = null;
        }
        if (receiverCandidatesUnsubRef.current) {
            receiverCandidatesUnsubRef.current();
            receiverCandidatesUnsubRef.current = null;
        }
        if (callListenerUnsubRef.current) {
            callListenerUnsubRef.current();
            callListenerUnsubRef.current = null;
        }

        webrtcService.cleanup();

        setCallState('idle');
        setActiveCall(null);
        setIsMuted(false);
        setRemoteStream(null);
        setIsSpeakerOn(false);

        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = null;
        }
    }, []);

    // Listen for incoming calls for the current user
    useEffect(() => {
        if (!currentUser) return;

        // Query for calls targeting this user that are still active
        const callsRef = collection(db, '1v1_calls');
        const q = query(
            callsRef,
            where('receiverId', '==', currentUser.uid),
            where('status', 'in', ['calling', 'ringing'])
        );

        callListenerUnsubRef.current = onSnapshot(q, async (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    // Check if it's a new incoming call
                    if (data.status === 'calling' && callState === 'idle') {
                        setActiveCall({ id: change.doc.id, ...data });
                        setCallState('ringing');
                    }
                }
            });
        });

        // Also listen for calls where current user is the caller
        const callerQuery = query(
            callsRef,
            where('callerId', '==', currentUser.uid),
            where('status', 'in', ['calling', 'connected'])
        );

        const callerUnsub = onSnapshot(callerQuery, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                const data = change.doc.data();
                if (change.type === 'modified') {
                    // If the call was accepted
                    if (data.status === 'connected' && activeCall?.id === change.doc.id) {
                        setCallState('connected');
                    }
                    // If the call was rejected or ended
                    if ((data.status === 'rejected' || data.status === 'ended') &&
                        activeCall?.id === change.doc.id) {
                        endCallSession();
                    }
                }
            });
        });

        return () => {
            if (callListenerUnsubRef.current) {
                callListenerUnsubRef.current();
                callListenerUnsubRef.current = null;
            }
            callerUnsub();
        };
    }, [currentUser, callState, activeCall, endCallSession]);

    // Initialize 1v1 Voice Call (Caller)
    const startCall = useCallback(async (receiverUser, channelId = null) => {
        if (!currentUser || callState !== 'idle') {
            console.warn('Cannot start call: current user not found or call already in progress');
            return;
        }

        setCallState('calling');

        try {
            await webrtcService.startLocalStream(false);

            const callDocRef = doc(collection(db, '1v1_calls'));

            webrtcService.createPeerConnection(
                async (candidate) => {
                    try {
                        await addDoc(collection(db, '1v1_calls', callDocRef.id, 'callerCandidates'), candidate);
                    } catch (err) {
                        console.error('Error saving ICE candidate:', err);
                    }
                },
                (stream) => {
                    setRemoteStream(stream);
                    if (remoteAudioRef.current) {
                        remoteAudioRef.current.srcObject = stream;
                    }
                }
            );

            const offer = await webrtcService.createOffer();

            const callData = {
                callerId: currentUser.uid,
                callerName: currentUser.nome || currentUser.displayName || currentUser.email || 'User',
                receiverId: receiverUser.uid,
                receiverName: receiverUser.nome || receiverUser.displayName || receiverUser.email || 'User',
                channelId: channelId || null,
                type: 'audio',
                status: 'calling',
                offer: { type: offer.type, sdp: offer.sdp },
                startedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            await setDoc(callDocRef, callData);
            setActiveCall({ id: callDocRef.id, ...callData });

            // Listen for Receiver's Answer
            callDocUnsubRef.current = onSnapshot(callDocRef, async (snapshot) => {
                const data = snapshot.data();
                if (!data) return;

                if (data.answer && callState !== 'connected') {
                    try {
                        await webrtcService.setRemoteAnswer(data.answer);
                        setCallState('connected');
                    } catch (err) {
                        console.error('Error setting remote answer:', err);
                    }
                }

                if (data.status === 'rejected') {
                    endCallSession();
                }

                if (data.status === 'ended') {
                    endCallSession();
                }
            });

            // Listen for Receiver Candidates
            receiverCandidatesUnsubRef.current = onSnapshot(
                collection(db, '1v1_calls', callDocRef.id, 'receiverCandidates'),
                (snapshot) => {
                    snapshot.docChanges().forEach((change) => {
                        if (change.type === 'added') {
                            webrtcService.addIceCandidate(change.doc.data());
                        }
                    });
                }
            );
        } catch (err) {
            console.error('Failed to start 1v1 call:', err);
            endCallSession();
            throw err;
        }
    }, [currentUser, callState, endCallSession]);

    // Accept Incoming Call (Receiver)
    const answerCall = useCallback(async () => {
        if (!activeCall || callState !== 'ringing') {
            console.warn('Cannot answer call: no active call or not in ringing state');
            return;
        }

        try {
            await webrtcService.startLocalStream(false);

            const callDocRef = doc(db, '1v1_calls', activeCall.id);

            webrtcService.createPeerConnection(
                async (candidate) => {
                    try {
                        await addDoc(collection(db, '1v1_calls', activeCall.id, 'receiverCandidates'), candidate);
                    } catch (err) {
                        console.error('Error saving ICE candidate:', err);
                    }
                },
                (stream) => {
                    setRemoteStream(stream);
                    if (remoteAudioRef.current) {
                        remoteAudioRef.current.srcObject = stream;
                    }
                }
            );

            const answer = await webrtcService.createAnswer(activeCall.offer);

            await updateDoc(callDocRef, {
                answer: { type: answer.type, sdp: answer.sdp },
                status: 'connected',
                answeredAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            setCallState('connected');

            // Listen for Caller Candidates
            callerCandidatesUnsubRef.current = onSnapshot(
                collection(db, '1v1_calls', activeCall.id, 'callerCandidates'),
                (snapshot) => {
                    snapshot.docChanges().forEach((change) => {
                        if (change.type === 'added') {
                            webrtcService.addIceCandidate(change.doc.data());
                        }
                    });
                }
            );

            // Listen for Call Termination
            callDocUnsubRef.current = onSnapshot(callDocRef, (snapshot) => {
                const data = snapshot.data();
                if (data && (data.status === 'ended' || data.status === 'rejected')) {
                    endCallSession();
                }
            });
        } catch (err) {
            console.error('Error answering call:', err);
            endCallSession();
        }
    }, [activeCall, callState, endCallSession]);

    // Reject Call (Receiver)
    const rejectCall = useCallback(async () => {
        if (!activeCall) return;

        try {
            const callDocRef = doc(db, '1v1_calls', activeCall.id);
            await updateDoc(callDocRef, {
                status: 'rejected',
                rejectedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error rejecting call:', err);
        } finally {
            endCallSession();
        }
    }, [activeCall, endCallSession]);

    // Hang Up Call (Caller or Receiver)
    const endCall = useCallback(async () => {
        if (!activeCall) {
            endCallSession();
            return;
        }

        try {
            const callDocRef = doc(db, '1v1_calls', activeCall.id);
            await updateDoc(callDocRef, {
                status: 'ended',
                endedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error ending call:', err);
        } finally {
            endCallSession();
        }
    }, [activeCall, endCallSession]);

    // Mute/Unmute
    const toggleMute = useCallback(() => {
        const newMuteState = !isMuted;
        setIsMuted(newMuteState);
        webrtcService.toggleMute(newMuteState);
    }, [isMuted]);

    // Toggle Speaker
    const toggleSpeaker = useCallback(() => {
        const newState = !isSpeakerOn;
        setIsSpeakerOn(newState);

        if (remoteAudioRef.current) {
            remoteAudioRef.current.setSinkId?.(newState ? 'default' : 'default');
        }
    }, [isSpeakerOn]);

    // Call duration tracking
    const [callDuration, setCallDuration] = useState(0);
    const durationIntervalRef = useRef(null);

    useEffect(() => {
        if (callState === 'connected') {
            setCallDuration(0);
            durationIntervalRef.current = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        } else {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
            if (callState === 'idle') {
                setCallDuration(0);
            }
        }

        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
        };
    }, [callState]);

    const formatDuration = useCallback((seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }, []);

    const value = {
        callState,
        activeCall,
        isMuted,
        remoteStream,
        isSpeakerOn,
        callDuration: formatDuration(callDuration),
        startCall,
        answerCall,
        rejectCall,
        endCall,
        toggleMute,
        toggleSpeaker,
        remoteAudioRef
    };

    return (
        <CallContext.Provider value={value}>
            {children}
        </CallContext.Provider>
    );
};

export default CallProvider;